/**
 * Owner: Haselton Baker Risk Group, LLC
 * Product: SP3 - SP3-Ui
 * Developer: Mike McGlone <mike@hbrisk.com>
 * Collaborators: Patrick Barnum <patrick@hbrisk.com>
 * Date: 11/21/17
 * Copyright All Rights Reserved
 */
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { compose, withProps } from 'recompose';
import { withScriptjs, withGoogleMap, GoogleMap, Marker, KmlLayer } from 'react-google-maps';
import config, { GOOGLE_MAPS_API_KEY } from 'src/js/support/config';
import googleMapURL from 'src/js/support/maps/googleMapURL';

export const EnhancedGoogleMap = withGoogleMap((
  { markers, center, defaultAnimation, urls, ...rest }
  ) => {
  const markerBlock = markers.map(marker => (
    <Marker
      {...marker}
      defaultAnimation={defaultAnimation}
    />
  ));

  const kmlBlock = urls.map(urlObj => (
    <KmlLayer
      {...urlObj}
      options={{ preserveViewport: true }}
    />
  ));

  return (
    <GoogleMap
      center={center}
      {...rest}
    >
      { markerBlock }
      { kmlBlock }
    </GoogleMap>
  );
});

const BaseMap = (
  { wrapperHeight, wrapperClassName, mapHeight, urls, markers, center, ...rest }
  ) => (
    <EnhancedGoogleMap
      containerElement={
        <div className={classNames(wrapperClassName)} style={{ height: wrapperHeight }} />
      }
      mapElement={
        <div style={{ height: mapHeight }} />
      }
      markers={markers}
      center={center}
      urls={urls}
      {...rest}
    />
);

BaseMap.defaultProps = {
  defaultZoom: 15,
  defaultAnimation: 2,
  mapHeight: '100%',
  markers: [],
  urls: [],
  wrapperClassName: '',
};

BaseMap.propTypes = {
  // The map's center
  center: PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number,
  }).isRequired,
  defaultAnimation: PropTypes.number,
  // The map's zoom level when it renders
  defaultZoom: PropTypes.number,
  // Height of the map element itself, a number (of pixels) or string corresponding to
  // a value of a height property with units
  mapHeight: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]).isRequired,
  // an array of objects, each of which contains data for a marker
  markers: PropTypes.arrayOf(
    PropTypes.object
  ),
  // an array of objects, each of which contains data for an overlay
  urls: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.number,
      url: PropTypes.string,
    })
  ),
  // class names for the wrapper component
  wrapperClassName: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
  ]),
  // Height of the element wrapping the map element, a number (of pixels) or string corresponding to
  // a value of a height property with units
  wrapperHeight: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]).isRequired,
};

export default compose(
  withProps({
    googleMapURL: googleMapURL(config(GOOGLE_MAPS_API_KEY)),
    loadingElement: <div />,
  }),
  withScriptjs
)(BaseMap);
